import classNames from "classnames"
import React, { useEffect, useState, useRef, Dispatch } from "react"

import styles from "./styles.module.scss"
import ValidityOkIcon from "../../../../assets/_V2/svg/ValidityOkIcon"
import CloseCross from "../../../../assets/_V2/svg/CloseCross"
import ChevronTop from "../../../../assets/_V2/svg/ChevronTop"
import Counter from "../../Counter"
import { TableCheckbox } from "../TableCheckbox"
import { pluralize } from "../../../../utils/pluralize"

export interface FeaturesTable {
	title: string | JSX.Element
	items: FeaturesTableRow[]
	footer?: string
}

export interface FeaturesTableRow {
	id: string,
	name: string | JSX.Element
	web?: string | boolean | JSX.Element
	start: string | boolean | JSX.Element
	standard: string | boolean | JSX.Element
	pro: string | boolean | JSX.Element
}

interface Props {
	className?: string
	data: FeaturesTable
	mobileSelectedTariff: string
	setModulesCost: Dispatch<any>
	isOpened: boolean
	currentPeriod: string
}

const TABLE_COLUMN_LABELS = {
	web: 'Web',
	start: 'Start',
	standard: 'Standard',
	pro: 'Pro'
}

export default function FeaturesTable(props: Props) {
	const [isTableOpened, setIsTableOpened] = useState(props.isOpened)

	const onClickHeader = () => {
		setIsTableOpened(!isTableOpened)
	}

	const featureTableEl = useRef("")

	const [openedTableHeight, setOpenedTableHeight] = useState(0)
	const [closedTableHeight, setClosedTableHeight] = useState(0)

	const tableHeight = isTableOpened ? openedTableHeight : closedTableHeight

	useEffect(() => {
		function calculateAccordionHeights() {
			let tableHeight: number = 0

			Array.from(featureTableEl.current.children).forEach((child, index) => {
				if (index === 0) {
					setClosedTableHeight(child.offsetHeight)
				}

				tableHeight = tableHeight + child.offsetHeight
			})

			setOpenedTableHeight(tableHeight)
		}

		calculateAccordionHeights()

		window.addEventListener("resize", calculateAccordionHeights)

		return () => {
			window.removeEventListener("resize", calculateAccordionHeights)
		}
	}, [props.mobileSelectedTariff])

	const renderFeatureContent = (
		featureContent: string | boolean | JSX.Element,
		tariff: string
	) => (
		<div
			className={classNames(styles.featuresTableRow__feature, {
				[styles.featuresTableRow__featureShowMobile]:
					props.mobileSelectedTariff === tariff,
			})}
		>
			{typeof featureContent === "boolean" ? (
				featureContent ? (
					<ValidityOkIcon />
				) : (
					<CloseCross className={styles.graySvg} />
				)
			) : (featureContent.month === false && props.currentPeriod === "month") ||
			(featureContent.month3 === false && props.currentPeriod === "month3") ||
			(featureContent.month6 === false && props.currentPeriod === "month6") ? (
				<CloseCross className={styles.graySvg} />
			)  : featureContent.type === "checkbox" ||
			  featureContent.type === "counter" ||
			featureContent.type === "value" ? (
				<>
					{featureContent.type === "checkbox" && (
						<TableCheckbox
							currentPeriod={props.currentPeriod}
							onChangeChecked={isChecked => {
								props.setModulesCost(tariff, isChecked, featureContent.price)
							}}
						/>
					)}
					{featureContent.type === "counter" && (
						<Counter
							currentPeriod={props.currentPeriod}
							min={0}
							max={featureContent.max || 99999999999}
							onPlus={() =>
								props.setModulesCost(tariff, true, featureContent.price)
							}
							onMinus={() =>
								props.setModulesCost(tariff, false, featureContent.price)
							}
						/>
					)}
					{featureContent.type === "value" && (
						<>
							{(props.currentPeriod === "month" ||
								props.currentPeriod === "month3" ||
								props.currentPeriod === "month6") && (
								featureContent.price.month ?
									featureContent.price.month :
									<CloseCross className={styles.graySvg}/>
							)}

							{props.currentPeriod === "year" && (
								featureContent.price.year ?
									featureContent.price.year :
									<CloseCross className={styles.graySvg}/>
							)}
						</>
					)}
				</>
			) : (
				featureContent
			)}
		</div>
	)

	return (
		<>
			<div
				className={classNames(styles.featuresTable, props.className)}
				ref={featureTableEl}
				style={{
					height: `${tableHeight}px`,
				}}
			>
				<div
					className={classNames(
						styles.featuresTableRow,
						styles.featuresTableRowHead,
						isTableOpened ? styles.opened : styles.closed
					)}
					onClick={onClickHeader}
				>
					<div className={styles.featuresTableRow__about}>
						{props.data.title}
					</div>

					<div className={styles.featuresTableRow__features}>
						{
							Object.keys(props.data.items[0]).map((featureKey: string) => {
								const columnLabel = TABLE_COLUMN_LABELS[featureKey as keyof typeof TABLE_COLUMN_LABELS];

								if (!columnLabel) return;

								return (
									<div
										key={columnLabel}
										className={classNames(
											styles.featuresTableRow__feature,
											styles[`featuresTableRow__feature${columnLabel}`]
										)}
									>
										{ TABLE_COLUMN_LABELS[featureKey as keyof typeof TABLE_COLUMN_LABELS] }
									</div>
								)
							})
						}
					</div>

					<span className={styles.featuresTableRow__count}>
						{props.data.items.length}{" "}
						{pluralize(
							"параметр|параметра|параметров",
							props.data.items.length
						)}
					</span>

					<button className={styles.featuresTableRow__button}>
						<ChevronTop />
					</button>
				</div>

				{props.data.items.map(feature => (
					<div className={styles.featuresTableRow} key={feature.id}>
						<div className={styles.featuresTableRow__about}>{feature.name}</div>

						<div className={styles.featuresTableRow__features}>
							{typeof feature.web !== 'undefined' && renderFeatureContent(feature.web, "web")}
							{renderFeatureContent(feature.start, "start")}
							{renderFeatureContent(feature.standard, "standard")}
							{renderFeatureContent(feature.pro, "pro")}
						</div>
					</div>
				))}

				{props.data.footer && (
					<div className={styles.featuresTableFooter}>{props.data.footer}</div>
				)}
			</div>
		</>
	)
}
